import { Bot, Check, MessageSquare } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, formatCount, formatRelative } from "../api";
import { Button, EmptyState, ErrorState, LoadingState } from "../components";
import { acknowledgementFor, initialsFor, kindLabel, type NoteInput, type NoteModel } from "./loopNotes";

const NOTE_BODY_MIN = 2;
const NOTE_BODY_MAX = 2_000;
const FAILED_STATUSES = new Set(["failed", "error"]);

export type LoopNotesState = {
  notes: NoteModel[];
  loading: boolean;
  error: string;
  post: (input: NoteInput) => Promise<NoteModel>;
  retry: () => void;
};

/** Loads the notes for one loop and exposes a poster that prepends the returned note. */
export function useLoopNotes(organizationId: string | undefined, loopId: string): LoopNotesState {
  const [notes, setNotes] = useState<NoteModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!organizationId || !loopId) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void api<{ notes: NoteModel[] }>(`/api/app/loops/${loopId}/notes`, { signal: controller.signal }, organizationId)
      .then((data) => setNotes(data.notes))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setError(caught instanceof Error ? caught.message : "Notes are unavailable.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [organizationId, loopId, refreshKey]);

  const post = useCallback(async (input: NoteInput) => {
    const { note } = await api<{ note: NoteModel }>(`/api/app/loops/${loopId}/notes`, { method: "POST", body: JSON.stringify(input) }, organizationId);
    setNotes((current) => [note, ...current.filter((existing) => existing.id !== note.id)]);
    return note;
  }, [organizationId, loopId]);

  const retry = useCallback(() => setRefreshKey((value) => value + 1), []);

  return { notes, loading, error, post, retry };
}

function formatAbsolute(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function NoteItem({ note }: { note: NoteModel }) {
  return (
    <li className={`loop-notes-item loop-notes-item--${note.kind}`}>
      <span className={`loop-notes-avatar loop-notes-avatar--${note.author.type}`} aria-hidden="true">{note.author.type === "agent" ? <Bot size={14} /> : initialsFor(note.author.name)}</span>
      <div>
        <header>
          <strong>{note.author.name}</strong>
          {note.author.type === "agent" ? <small>agent</small> : null}
          {!note.author.sameWorkspace ? <small className="loop-notes-origin">{note.author.organizationName}</small> : null}
          <span className={`loop-notes-kind loop-notes-kind--${note.kind}`}>{kindLabel(note.kind)}</span>
          <time dateTime={note.createdAt} title={formatAbsolute(note.createdAt)}>{formatRelative(note.createdAt)}</time>
        </header>
        <p>{note.body}</p>
      </div>
    </li>
  );
}

/**
 * The Notes panel on loop detail: thread, empty state, and composer. Read-only viewers of shared loops may still post.
 * `embedded` drops the panel chrome and head so the page's own section row names it.
 */
export function NotesPanel({ state, embedded = false }: { state: LoopNotesState; embedded?: boolean }) {
  const { notes, loading, error, post, retry } = state;
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const trimmed = body.trim();
  const canPost = trimmed.length >= NOTE_BODY_MIN && trimmed.length <= NOTE_BODY_MAX && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canPost) return;
    setBusy(true);
    setFormError("");
    try {
      await post({ kind: "note", body: trimmed });
      setBody("");
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : "The note could not be posted.");
    } finally {
      setBusy(false);
    }
  }

  const Wrapper = embedded ? "div" : "section";
  return (
    <Wrapper className={embedded ? "loop-notes-panel loop-notes-panel--embedded" : "panel cockpit-panel loop-notes-panel"} aria-label={embedded ? undefined : "Loop notes"}>
      {!embedded ? (
        <div className="panel__head">
          <div><p className="eyebrow">People and agents</p><h2>Notes</h2></div>
          <span className="loop-notes-count"><MessageSquare size={12} aria-hidden="true" /> {formatCount(notes.length, "note")}</span>
        </div>
      ) : null}
      {loading ? (
        <LoadingState>Reading notes…</LoadingState>
      ) : error ? (
        <ErrorState action={<Button variant="secondary" onClick={retry}>Try again</Button>}>Notes unavailable: {error}</ErrorState>
      ) : notes.length > 0 ? (
        <ol className="loop-notes-thread">{notes.map((note) => <NoteItem key={note.id} note={note} />)}</ol>
      ) : (
        <EmptyState eyebrow="Open thread" title="No notes yet" action={<Button variant="secondary" onClick={() => textareaRef.current?.focus()}>Write a note</Button>}>
          Leave one for your teammates or your agents.
        </EmptyState>
      )}
      <form className="loop-notes-composer" onSubmit={submit}>
        <label>
          <span className="sr-only">New note</span>
          <textarea ref={textareaRef} value={body} onChange={(event) => setBody(event.target.value)} placeholder="Leave a note for your teammates or your agents" rows={3} maxLength={NOTE_BODY_MAX} disabled={busy} />
        </label>
        {formError ? <div className="form-error" role="alert">{formError}</div> : null}
        <div className="loop-notes-composer__actions">
          <small>{trimmed.length < NOTE_BODY_MIN ? `At least ${NOTE_BODY_MIN} characters` : `${trimmed.length.toLocaleString()} / ${NOTE_BODY_MAX.toLocaleString()}`}</small>
          <Button type="submit" busy={busy} disabled={!canPost}>Post note</Button>
        </div>
      </form>
    </Wrapper>
  );
}

/**
 * Sits under a run row. A failed run gets a quiet Acknowledge action; once acknowledged (from the
 * notes list) the row shows who acknowledged it and when. Other runs render nothing.
 */
export function RunAcknowledgement({ runId, status, notes, onAcknowledge }: { runId: string; status: string; notes: NoteModel[]; onAcknowledge: (runId: string) => Promise<unknown> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const acknowledgement = acknowledgementFor(notes, runId);

  if (acknowledgement) {
    const origin = acknowledgement.author.sameWorkspace ? "" : ` · ${acknowledgement.author.organizationName}`;
    return (
      <div className="loop-notes-run-ack loop-notes-run-ack--done">
        <Check size={12} aria-hidden="true" />
        <span>Acknowledged by {acknowledgement.author.name}{origin} · <time dateTime={acknowledgement.createdAt} title={formatAbsolute(acknowledgement.createdAt)}>{formatRelative(acknowledgement.createdAt)}</time></span>
      </div>
    );
  }
  if (!FAILED_STATUSES.has(status.trim().toLowerCase())) return null;

  async function acknowledge() {
    setBusy(true);
    setError("");
    try {
      await onAcknowledge(runId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The run could not be acknowledged.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="loop-notes-run-ack">
      {error ? <small role="alert">{error}</small> : null}
      <Button variant="ghost" busy={busy} onClick={() => void acknowledge()}>Acknowledge</Button>
    </div>
  );
}
